// The Hazards switch, first row of the Maps sheet's More view (Maps tab → More): whether
// it shows, and what one flip does. Kept out of the component, with the layer, the switch
// and analytics injected, so the rules run under Jest without a map or a store.
//
// It is the same switch the Overlays tab's Hazards card used to be: localStorage
// 'hazardsVisible', on by default, free (never Pro-gated), shared by every map dialog on
// the page.

import { HazardAnalyticsEvent, hazardsToggledEvent } from './hazardAnalytics';

/** What the row needs to know about the hazard layer (sharedHazardLayer()). */
export interface HazardsSwitchLayer {
  /** Signed in: the hazard tiles need a session, so a signed-out visitor gets no row. */
  isAvailable(): boolean;
  /** The stored switch, read when the More view opens. */
  isEnabled(): boolean;
}

/**
 * Whether the More view shows the Hazards row: on a map that carries hazard markers (the
 * dialog's showHazards — off for the 3D peak preview) and for a signed-in walker. Being
 * Pro is never a condition.
 */
export function showsHazardsSwitch(showHazards: boolean, layer: Pick<HazardsSwitchLayer, 'isAvailable'>): boolean {
  return !!showHazards && !!layer.isAvailable();
}

/** The row's state as the view renders: the layer's own switch, which owns the storage. */
export function hazardsSwitchState(layer: Pick<HazardsSwitchLayer, 'isEnabled'>): boolean {
  return !!layer.isEnabled();
}

export interface HazardsSwitchDeps {
  /** Switches the markers, the stored choice and the store's mirror (toggleHazardsOverlay). */
  setEnabled: (enabled: boolean) => unknown;
  /** Sends one analytics event (trackHazardEvent). */
  track: (event: HazardAnalyticsEvent) => void;
}

/**
 * The walker set the switch to `enabled`: switch the markers and send hazards_toggled. The
 * store action sends nothing itself, because "On this map" uses it to clear and Undo too.
 * Returns the state the row now shows.
 */
export function setHazardsSwitch(enabled: boolean, deps: HazardsSwitchDeps): boolean {
  const next = !!enabled;
  deps.setEnabled(next);
  deps.track(hazardsToggledEvent(next));
  return next;
}
